import {
    type SavedItinerariesState,
    type SavedItineraryEntry,
} from "../store/persistence/slices/savedItineraries.slice";
import { type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";

/** Maximum number of saved itineraries kept locally (oldest entries are evicted). */
export const SAVED_ITINERARIES_MAX_ITEMS = 20;

/**
 * Clones an itinerary snapshot for persistence so later in-memory mutations do not alter saved itineraries.
 *
 * @param itinerary Live itinerary selected in the planner.
 * @returns Deep copy safe to store in the persistence slice.
 */
const cloneItineraryForPersistence = ( itinerary: ItineraryDTO ): ItineraryDTO => {
    if ( typeof structuredClone === "function" ) {
        return structuredClone( itinerary );
    }

    return JSON.parse( JSON.stringify( itinerary ) ) as ItineraryDTO;
};

/**
 * Ensures an itinerary can be stored as a saved itinerary.
 *
 * @param itinerary Candidate itinerary from itinerary details.
 * @throws When the itinerary id is missing.
 */
export const assertPersistableItinerary = ( itinerary: ItineraryDTO ): void => {
    const itineraryId = itinerary.id?.trim();
    if ( itineraryId == null || itineraryId === "" ) {
        throw new Error( "Cannot save an itinerary without a stable id." );
    }
};

/**
 * Returns whether an itinerary id is already saved.
 *
 * @param savedItineraries Current persisted saved itineraries.
 * @param itineraryId Itinerary id to look up.
 * @returns `true` when a saved itinerary exists for the id.
 */
export const isItinerarySaved = (
    savedItineraries: SavedItinerariesState,
    itineraryId: string | null | undefined,
): boolean => {
    if ( itineraryId == null || itineraryId.trim() === "" ) {
        return false;
    }

    return savedItineraries.some( ( item ) => item.itinerary.id === itineraryId );
};

/**
 * Removes a saved itinerary entry by itinerary id.
 *
 * @param savedItineraries Current persisted saved itineraries.
 * @param itineraryId Itinerary id to remove.
 * @returns Updated saved itinerary list without the matching entry.
 */
export const removeSavedItinerary = (
    savedItineraries: SavedItinerariesState,
    itineraryId: string,
): SavedItinerariesState => {
    return savedItineraries.filter( ( item ) => item.itinerary.id !== itineraryId );
};

/** Optional user-facing route labels captured when saving an itinerary. */
export interface SavedItineraryRouteLabels {
    "originLabel"?:      string;
    "destinationLabel"?: string;
}

/**
 * Toggles a saved itinerary entry for the given itinerary snapshot.
 *
 * @param savedItineraries Current persisted saved itineraries.
 * @param itinerary Full itinerary DTO to persist when not already saved.
 * @param routeLabels Optional departure/arrival labels from the active search context.
 * @returns Updated saved itinerary list and whether the item is saved after the toggle.
 */
export const toggleSavedItinerary = (
    savedItineraries: SavedItinerariesState,
    itinerary: ItineraryDTO,
    routeLabels?: SavedItineraryRouteLabels,
): {
    "nextSavedItineraries": SavedItinerariesState;
    "isSaved":              boolean;
} => {
    assertPersistableItinerary( itinerary );

    const existingIndex = savedItineraries.findIndex( ( savedItem ) => savedItem.itinerary.id === itinerary.id );

    if ( existingIndex !== -1 ) {
        const nextSavedItineraries = savedItineraries.filter( ( _, index ) => index !== existingIndex );

        return {
            nextSavedItineraries,
            "isSaved": false,
        };
    }

    const nextEntry: SavedItineraryEntry = {
        "itinerary": cloneItineraryForPersistence( itinerary ),
        "savedAt":   Date.now(),
        ...routeLabels?.originLabel !== undefined ? { "originLabel": routeLabels.originLabel } : {},
        ...routeLabels?.destinationLabel !== undefined ? { "destinationLabel": routeLabels.destinationLabel } : {},
    };
    const nextSavedItineraries = [
        nextEntry,
        ...savedItineraries,
    ].slice( 0, SAVED_ITINERARIES_MAX_ITEMS );

    return {
        nextSavedItineraries,
        "isSaved": true,
    };
};
